import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Home, LogOut, User, Gift, Clapperboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SiteLogo } from "../components/SiteLogo";
import GiftDesigner from "../components/member/GiftDesigner";
import Profile from "../components/member/Profile";
import VideoArchive from "../components/member/VideoArchive";

const NAV = [
  { key: "profile", label: "Profil", icon: User, testid: "member-nav-profile" },
  { key: "designer", label: "Hediye Tasarımı", icon: Gift, testid: "member-nav-designer" },
  { key: "videos", label: "Video Arşiv", icon: Clapperboard, testid: "member-nav-videos" },
];

export default function MemberPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");

  if (user?.role === "admin") return <Navigate to="/admin" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const titles = {
    profile: <>Pro<span className="text-gradient">fil</span></>,
    designer: <>Hediye <span className="text-gradient">Tasarımı</span></>,
    videos: <>Video <span className="text-gradient">Arşiv</span></>,
  };

  return (
    <div data-testid="member-panel" className="min-h-screen bg-[#050505] text-white flex">
      <div className="fixed top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#0047FF]/8 blur-[140px] pointer-events-none" />

      <aside className="hidden lg:flex flex-col w-64 shrink-0 fixed inset-y-0 left-0 z-40 bg-[#0A0E17] border-r border-white/5">
        <div className="flex items-center px-6 h-16 border-b border-white/5">
          <SiteLogo />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1" data-testid="member-sidebar-nav">
          {NAV.map((item) => (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              data-testid={item.testid}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13px] font-medium transition-colors ${
                tab === item.key
                  ? "bg-gradient-to-r from-[#0047FF]/25 to-[#00F0FF]/15 text-white border border-cyan-400/25"
                  : "text-[#A0ABC0] hover:text-white hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <item.icon size={17} strokeWidth={1.5} />
              <span className="flex-1 text-left">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/5 space-y-1">
          <Link to="/" data-testid="member-home-button"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13px] font-medium text-[#A0ABC0] hover:text-white hover:bg-white/[0.04] transition-colors">
            <Home size={17} strokeWidth={1.5} /> Ana Sayfa
          </Link>
          <button onClick={handleLogout} data-testid="member-logout-button"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13px] font-medium text-[#A0ABC0] hover:text-red-300 hover:bg-red-500/10 transition-colors">
            <LogOut size={17} strokeWidth={1.5} /> Çıkış Yap
          </button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-16 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-5">
        <SiteLogo />
        <div className="flex items-center gap-2.5">
          <Link to="/" data-testid="member-home-button-mobile" aria-label="Ana Sayfa"
            className="w-9 h-9 rounded-full border border-white/15 bg-black/40 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-200 hover:border-cyan-400/50 transition-colors">
            <Home size={16} strokeWidth={1.5} />
          </Link>
          <button onClick={handleLogout} data-testid="member-logout-button-mobile" aria-label="Çıkış Yap"
            className="w-9 h-9 rounded-full border border-white/15 bg-black/40 flex items-center justify-center text-[#A0ABC0] hover:text-red-300 hover:border-red-400/50 transition-colors">
            <LogOut size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <main className="relative flex-1 lg:ml-64 min-w-0 pt-16 lg:pt-0 pb-20 lg:pb-0">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
          <div className="mb-8">
            <h1 className="font-display font-700 text-2xl md:text-3xl text-white tracking-tight" data-testid="member-page-title">
              {titles[tab]}
            </h1>
          </div>
          {tab === "profile" ? <Profile /> : tab === "designer" ? <GiftDesigner /> : <VideoArchive />}
        </div>
      </main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 h-16 bg-[#0A0E17]/90 backdrop-blur-md border-t border-white/5 flex justify-around items-center" data-testid="member-bottom-nav">
        {NAV.map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            data-testid={`${item.testid}-mobile`}
            aria-label={item.label}
            className={`flex flex-col items-center gap-1 px-4 py-2 transition-colors ${tab === item.key ? "text-cyan-300" : "text-[#64748B]"}`}
          >
            <item.icon size={20} strokeWidth={1.5} />
            <span className="text-[10px] font-medium">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
